import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import type { Tier, RaceType, CarClass } from "system";

const twMerge = extendTailwindMerge<'text-outline'>({
  extend: {
    classGroups: {
      'text-outline': [{ 'text-outline': [() => true] }],
    },
  }
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Tier ──────────────────────────────────────────────────────────────────────

export interface TierStyle {
  text: string;
  bg: string;
  border: string;
  glow: string;
  glowbg: string;
}

export const TIER_COLORS: Record<
  Tier,
  TierStyle
> = {
  white: {
    text: "text-tier-white",
    bg: "bg-tier-white/10",
    border: "border-tier-white/30",
    glow: "shadow-tier-white/20",
    glowbg: "bg-tier-white",
  },
  yellow: {
    text: "text-tier-yellow",
    bg: "bg-tier-yellow/10",
    border: "border-tier-yellow/30",
    glow: "transparent",
    glowbg: "bg-tier-yellow",
  },
  pink: {
    text: "text-tier-pink",
    bg: "bg-tier-pink/10",
    border: "border-tier-pink/30",
    glow: "transparent",
    glowbg: "bg-tier-pink",
  },
  green: {
    text: "text-tier-green",
    bg: "bg-tier-green/10",
    border: "border-tier-green/30",
    glow: "transparent",
    glowbg: "bg-tier-green",
  },
  orange: {
    text: "text-tier-orange",
    bg: "bg-tier-orange/10",
    border: "border-tier-orange/30",
    glow: "transparent",
    glowbg: "bg-tier-orange",
  },
  blue: {
    text: "text-tier-blue",
    bg: "bg-tier-blue/10",
    border: "border-tier-blue/30",
    glow: "transparent",
    glowbg: "bg-tier-blue",
  },
  gray: {
    text: "text-tier-gray",
    bg: "bg-tier-gray/10",
    border: "border-tier-gray/30",
    glow: "transparent",
    glowbg: "bg-tier-gray",
  },
  purple: {
    text: "text-tier-purple",
    bg: "bg-tier-purple/10",
    border: "border-tier-purple/30",
    glow: "transparent",
    glowbg: "bg-tier-purple",
  },
  red: {
    text: "text-tier-red",
    bg: "bg-tier-red/10",
    border: "border-tier-red/30",
    glow: "transparent",
    glowbg: "bg-tier-red",
  },
  black: {
    text: "text-tier-black-text",
    bg: "bg-tier-black",
    border: "border-tier-black-text/30",
    glow: "ring ring-tier-black-text",
    glowbg: "bg-tier-black/20",
  },
};

// Label maps live in `system` so the Discord bot (server) and the frontend share
// one definition. Re-exported here so existing `~/lib/utils` imports keep working.
export { RACE_TYPE_LABELS, CAR_CLASS_LABELS, SEAL_LABELS } from "system";

// ── Race-type theming ─────────────────────────────────────────────────────────

export const RACE_TYPE_CARD_COLOR = {
  road: "blue",
  street: "purple",
  cross_country: "green",
  dirt: "orange",
} satisfies Record<RaceType, string>;

export const RACE_TYPE_FRAME_BORDER = {
  road: 'road',
  street: 'street',
  cross_country: 'cc',
  dirt: 'dirt',
} satisfies Record<RaceType, string>;

export const RACE_TYPE_COLOR_VAR = {
  road: "var(--color-tier-blue)",
  street: "var(--color-tier-purple)",
  cross_country: "var(--color-tier-green)",
  dirt: "var(--color-tier-orange)",
} satisfies Record<RaceType, string>;

export const CAR_CLASS_COLOR_VAR = {
  d: "#93c5fd",
  c: "var(--color-tier-yellow)",
  b: "var(--color-tier-orange)",
  a: "var(--color-tier-red)",
  s1: "var(--color-tier-purple)",
  s2: "var(--color-tier-blue)",
  x: "var(--color-tier-green)",
  r: "#cd7f32",
} satisfies Record<CarClass, string>;

// ── Dates ─────────────────────────────────────────────────────────────────────

export function timeUntil(date: string | Date): string {
  const ms = new Date(date).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Relative past time — "3 days ago", "yesterday", "2 months ago". Delegates the
// wording and pluralisation to the platform Intl formatter.
const RELATIVE_TIME = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

export function timeAgo(date: string | Date): string {
  const sec = (new Date(date).getTime() - Date.now()) / 1000; // negative in the past
  const abs = Math.abs(sec);
  if (abs < 60) return RELATIVE_TIME.format(Math.round(sec), "second");
  if (abs < 3_600) return RELATIVE_TIME.format(Math.round(sec / 60), "minute");
  if (abs < 86_400) return RELATIVE_TIME.format(Math.round(sec / 3_600), "hour");
  if (abs < 2_592_000) return RELATIVE_TIME.format(Math.round(sec / 86_400), "day");
  if (abs < 31_536_000) return RELATIVE_TIME.format(Math.round(sec / 2_592_000), "month");
  return RELATIVE_TIME.format(Math.round(sec / 31_536_000), "year");
}
